import React, {createContext, useContext} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {AppDispatch, RootState} from '../../../../store/configureStore';
import {isEmail} from '../../../../common/utils';
import {setUser} from '../../../Auth';

export type UserDetailsFormValues = {
  email: string,
  phone: string,
  city: string,
  firstName: string,
  lastName: string,
  address: string,
}

export interface UserDetailsFormContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext = {
  initialValues: {
    email: '',
    phone: '',
    city: '',
    firstName: '',
    lastName: '',
    address: '',
  },
};

export const UserDetailsFormContext = createContext(defaultContext);
UserDetailsFormContext.displayName = 'UserDetailsFormContext';

export const useUserDetailsFormContext = () => useContext(UserDetailsFormContext);

export const UserDetailsFormContextProvider = ({children}: UserDetailsFormContextProviderProps) => {
  const M = useMessage();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const dispatch: AppDispatch = useDispatch();
  const initialUserDetails = useSelector(
    (state: RootState) => state.auth.user,
    shallowEqual
  );
  const getEmailInitialValue = () => {
    if (initialUserDetails?.emails && initialUserDetails?.emails.length > 0) {
      return initialUserDetails?.emails[0].address
    } else return ''
  };

  const formik = useFormik<UserDetailsFormValues>({
    initialValues: {
      email: getEmailInitialValue(),
      phone: initialUserDetails?.profile?.phone || '',
      city: initialUserDetails?.profile?.city || '',
      firstName: initialUserDetails?.profile?.firstName || '',
      lastName: initialUserDetails?.profile?.lastName || '',
      address: initialUserDetails?.profile?.address || '',
    },
    validate: (values: UserDetailsFormValues) => {
      let errors: Partial<UserDetailsFormValues> = {};
      if (!values.email || values.email.length === 0) {
        errors = {...errors, email: M.requiredField};
      }
      if (values.email && values.email.length > 0 && !isEmail(values.email)) {
        errors = {...errors, email: M.notValidEmail};
      }
      if (!values.phone) {
        errors = {...errors, phone: M.requiredField};
      }
      if (!values.address) {
        errors = {...errors, address: M.requiredField};
      }
      if (!values.city) {
        errors = {...errors, city: M.requiredField};
      }
      if (!values.firstName) {
        errors = {...errors, firstName: M.requiredField};
      }
      if (!values.lastName) {
        errors = {...errors, lastName: M.requiredField};
      }
      if (!values.address) {
        errors = {...errors, address: M.requiredField};
      }

      return errors;
    },
    onSubmit: async (values: UserDetailsFormValues) => {
      try {
        const result = await apiService.changeUserDetails({
          email: values.email,
          phone: values.phone,
          city: values.city,
          firstName: values.firstName,
          lastName: values.lastName,
          address: values.address,
        });
        if (result.errorCode === 0) {
          globalMessage.open(M.userDetailsSuccess);
          dispatch(setUser({
            _id: initialUserDetails?._id || '',
            username: initialUserDetails?.username || '',
            agentId: initialUserDetails?.agentId || '',
            profile: {
              idNumber: initialUserDetails?.profile?.idNumber || '',
              firstName: values.firstName,
              lastName: values.lastName,
              city: values.city,
              address: values.address,
              phone: values.phone,
            },
            receiveNotifications: Boolean(initialUserDetails?.receiveNotifications),
            woltEmployee: Boolean(initialUserDetails?.woltEmployee),
            sellerPaymeId: initialUserDetails?.sellerPaymeId || '',
            tamalId: initialUserDetails?.tamalId || '',
            affiliateCompanyID: initialUserDetails?.affiliateCompanyID || '',
            emails: [{
              address: values.email || '',
              verified: Boolean(initialUserDetails?.emails[0].verified),
            }],
            forcedCustomerID: initialUserDetails?.forcedCustomerID || '',
            invoiceOrdersExceptionFillEmail: Boolean(initialUserDetails?.invoiceOrdersExceptionFillEmail),
            paymentPageNotAllowed: Boolean(initialUserDetails?.paymentPageNotAllowed),
            invoiceOrderPageNotAllowed: Boolean(initialUserDetails?.invoiceOrderPageNotAllowed),
          }))
        }
        if (result.errorCode < 0) {
          globalMessage.open(M.serverError);
        }
      } catch (e) {
        console.error(e);
        globalMessage.open(M.serverError);
      }
    },
    enableReinitialize: true,
  });

  const context = {
    ...defaultContext
  };

  return (
    <UserDetailsFormContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </UserDetailsFormContext.Provider>
  );
};